import type {
    DemandFilterInput,
    DemandGroup,
    Order,
    SampleOrderCardOrder,
    SampleOrderDemand,
    Style,
    TableStyleFragment,
} from '@buying/library';

/**
 * This type takes any object, as long as it has the collectionNumber, collectionTerm and collectionName
 */
interface ColoursCardCollection {
    [key: string]: unknown;
    collectionNumber: number;
    collectionTerm: string;
    collectionName: string;
}

/**
 * This type takes any object, as long as it has the brandName and brandNumber
 */
interface CardBrand {
    [key: string]: unknown;
    brandName: string;
    brandNumber: string;
}

interface ColoursCardMountData {
    collection: ColoursCardCollection;
    brand: CardBrand;
}

interface ColoursCardConfig {
    id: string;
    card: 'colours';
    mountData: ColoursCardMountData;
    onClose?: () => void;
}

interface StyleCardConfig {
    id: string;
    card: 'style';
    mountData: Pick<Style, 'styleNumber'> | TableStyleFragment;
    onClose?: () => void;
}

interface CreateOrdersCardMountData {
    demandGroup: DemandGroup;
    filters: DemandFilterInput;
    brand: CardBrand;
}

interface CreateOrdersCardConfig {
    id: string;
    card: 'create orders';
    mountData: CreateOrdersCardMountData;
    onClose?: () => void;
    onHide?: () => void;
}

interface OrderCardMountData {
    brand: CardBrand;
    order: Order;
}

interface OrderCardConfig {
    id: string;
    card: 'order';
    mountData: OrderCardMountData;
    onClose?: () => void;
}

interface CreateSampleOrdersCardMountData {
    brand: CardBrand;
    collectionNumber: number;
    collectionName: string;
    supplierNumber: string;
    styles: Array<SampleOrderDemand>;
}

interface CreateSampleOrdersCardConfig {
    id: string;
    card: 'create sample orders';
    mountData: CreateSampleOrdersCardMountData;
    onClose?: () => void;
    onHide?: () => void;
}

/** A sample order enriched with its style, used as the sample order card mount data. */
interface SampleOrderCardMountData {
    brand: CardBrand;
    sampleOrder: SampleOrderCardOrder;
}

interface SampleOrderCardConfig {
    id: string;
    card: 'sample order';
    mountData: SampleOrderCardMountData;
    onClose?: () => void;
}

type CardConfig =
    | ColoursCardConfig
    | CreateOrdersCardConfig
    | CreateSampleOrdersCardConfig
    | OrderCardConfig
    | SampleOrderCardConfig
    | StyleCardConfig;

// Type for the state of the snackbar reducer, which is an array of snackbar configurations
type CardLayersReducerState = Array<CardConfig>;

type CardLayersOpenPayload =
    | Omit<ColoursCardConfig, 'id'>
    | Omit<CreateOrdersCardConfig, 'id'>
    | Omit<CreateSampleOrdersCardConfig, 'id'>
    | Omit<OrderCardConfig, 'id'>
    | Omit<SampleOrderCardConfig, 'id'>
    | Omit<StyleCardConfig, 'id'>;

// Type for the actions that can be dispatched to the snackbar reducer
type CardLayersReducerAction =
    | { type: 'close_all' }
    | { type: 'close'; payload: { id: string } }
    | { type: 'open'; payload: CardLayersOpenPayload };

const cardLayersReducer = (
    state: Array<CardConfig>,
    action: CardLayersReducerAction
): CardLayersReducerState => {
    switch (action.type) {
        case 'open':
            // If the card is a style card, we have a little extra logic
            if (action.payload.card === 'style') {
                // First we check if this style is already in the state
                const duplicateStyleCardIndex = state.findIndex(
                    (s) =>
                        s.card === 'style' &&
                        'styleNumber' in action.payload.mountData &&
                        s.mountData.styleNumber ===
                            action.payload.mountData.styleNumber
                );
                if (duplicateStyleCardIndex !== -1) {
                    // If a duplicate was found, re-sort it so it's at the end of the array (and therefore the card is on top)
                    return state.slice().sort((a, b) => {
                        if (a.id === state[duplicateStyleCardIndex].id)
                            return 1;
                        if (b.id === state[duplicateStyleCardIndex].id)
                            return -1;
                        return 0;
                    });
                }
            }

            // If the card is not a style card, just add it to the end of the array
            return [...state, { ...action.payload, id: crypto.randomUUID() }];
        case 'close':
            return state.filter((s) => s.id !== action.payload.id);
        case 'close_all':
            return [];
        default:
            return state;
    }
};

export { cardLayersReducer };
export type {
    CardBrand,
    CardConfig,
    CardLayersReducerAction,
    CardLayersReducerState,
    ColoursCardCollection,
    ColoursCardConfig,
    CreateOrdersCardConfig,
    CreateSampleOrdersCardConfig,
    CreateSampleOrdersCardMountData,
    SampleOrderCardConfig,
    SampleOrderCardMountData,
    StyleCardConfig,
    OrderCardConfig,
    OrderCardMountData,
    CreateOrdersCardMountData,
};
